import React, { useState, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Paper,
  Divider,
  Avatar,
  Stack,
  IconButton,
  Fade,
  useTheme,
  alpha,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CloudUpload,
  Delete as DeleteIcon,
  PhotoCamera,
  Image as ImageIcon,
  CheckCircle,
  LocationOn,
  MyLocation,
  Person,
  Visibility,
  GetApp,
  Security,
  ArrowForward,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { apiService, ImageUploadData } from '../services/apiService';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import ImageViewerModal from '../components/ImageViewerModal';

const bodyPartOptions = [
  'Brazo izquierdo',
  'Brazo derecho',
  'Tórax',
  'Espalda',
  'Pierna izquierda',
  'Pierna derecha',
  'Cuello',
  'Cabeza/Cara',
  'Otra',
  'No especificar',
];

const uploadSchema = yup.object().shape({
  age: yup.number().min(0).max(120).optional(),
  body_part: yup.string().optional(),
  diameter_larger_than_pencil: yup.boolean().required(),
  geolocation_city: yup.string().optional(),
  geolocation_region: yup.string().optional(),
  additional_comment: yup.string().optional(),
});

const UploadPage: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm({
    resolver: yupResolver(uploadSchema),
    defaultValues: {
      age: undefined,
      body_part: 'No especificar',
      diameter_larger_than_pencil: false,
      geolocation_city: '',
      geolocation_region: '',
      additional_comment: '',
    },
  });

  // Function to get location from device
  const getDeviceLocation = async () => {
    setIsLoadingLocation(true);

    if (!navigator.geolocation) {
      alert('Geolocation is not supported by your browser');
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          // Use OpenStreetMap Nominatim API for reverse geocoding
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&accept-language=es`,
            {
              headers: {
                'User-Agent': 'SkinBot-MelanomaDetection/1.0'
              }
            }
          );

          if (response.ok) {
            const data = await response.json();
            const address = data.address;

            // Extract city and region from the response
            const city = address.city || address.town || address.village || address.municipality || '';
            const region = address.state || address.province || address.region || '';

            // Update form values
            form.setValue('geolocation_city', city);
            form.setValue('geolocation_region', region);
          } else {
            console.error('Failed to get location details');
          }
        } catch (error) {
          console.error('Error fetching location details:', error);
        } finally {
          setIsLoadingLocation(false);
        }
      },
      (error) => {
        console.error('Error getting location:', error);
        let errorMessage = 'Unable to retrieve your location';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }

        alert(errorMessage);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  const uploadMutation = useMutation({
    mutationFn: async (data: { file: File; formData: ImageUploadData }) => {
      // Simulate upload progress
      setUploadProgress(0);
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const result = await apiService.uploadImage(data.file, data.formData);
      clearInterval(progressInterval);
      setUploadProgress(100);

      setTimeout(() => setUploadProgress(0), 2000);
      return result;
    },
    onSuccess: (result) => {
      setUploadResult(result);
      form.reset();
      setSelectedFile(null);
      setPreviewUrl(null);
    },
  });

  const validateAndSetFile = useCallback((file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return false;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return false;
    }

    setSelectedFile(file);

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return true;
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  }, [validateAndSetFile]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      validateAndSetFile(files[0]);
    }
  }, [validateAndSetFile]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
  };

  const onSubmit = async (data: any) => {
    if (!selectedFile) {
      alert('Please select an image file');
      return;
    }

    if (!user) {
      alert('User not authenticated');
      return;
    }

    const uploadData: ImageUploadData = {
      username: user.username,
      age: data.age,
      body_part: data.body_part === 'No especificar' ? '' : data.body_part,
      diameter_larger_than_pencil: data.diameter_larger_than_pencil,
      geolocation_city: data.geolocation_city,
      geolocation_region: data.geolocation_region,
      additional_comment: data.additional_comment,
      timestamp: new Date().toISOString(),
    };

    uploadMutation.mutate({ file: selectedFile, formData: uploadData });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <Box>
        {/* Header Section */}
        <Fade in={true} timeout={800}>
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              📸 Skin Lesion Analysis
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, mb: 3 }}>
              AI-powered preliminary evaluation for preventive healthcare monitoring
            </Typography>

            {/* Enhanced Medical Disclaimer */}
            <Alert
              severity="info"
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.05)} 0%, ${alpha(theme.palette.info.main, 0.02)} 100%)`,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                '& .MuiAlert-icon': {
                  fontSize: '1.5rem',
                },
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                <strong>⚠️ Medical Disclaimer:</strong> This AI tool provides preliminary evaluation for educational and preventive monitoring purposes only.
                It does NOT constitute medical diagnosis and should not replace professional medical evaluation.
                Always consult with qualified healthcare professionals for proper skin health assessment.
              </Typography>
            </Alert>
          </Box>
        </Fade>

        {/* Upload Result */}
        <AnimatePresence>
          {uploadResult && (
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: 4,
                  mb: 4,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.info.main} 100%)`,
                  },
                }}
              >
                <Stack direction="row" spacing={3} alignItems="flex-start">
                  <Avatar sx={{ bgcolor: theme.palette.success.main, width: 64, height: 64 }}>
                    <CheckCircle sx={{ fontSize: '2rem' }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.success.main, mb: 1 }}>
                      ✅ Analysis Complete!
                    </Typography>
                    <Typography variant="h6" gutterBottom>
                      <strong>Preliminary Classification:</strong> {uploadResult.first_classification}
                    </Typography>

                    {/* View Image Button */}
                    {uploadResult.image_b64 && (
                      <Box sx={{ mt: 3, mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={<ImageIcon />}
                          onClick={() => setImageModalOpen(true)}
                          sx={{
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            '&:hover': {
                              borderColor: theme.palette.primary.dark,
                              background: alpha(theme.palette.primary.main, 0.05),
                            },
                          }}
                        >
                          View Segmentation Result
                        </Button>
                      </Box>
                    )}

                    <Alert
                      severity="warning"
                      sx={{
                        mt: 3,
                        borderRadius: 2,
                        background: alpha(theme.palette.warning.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        ⚠️ <strong>Important:</strong> This is a preliminary automated evaluation for screening purposes only.
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 400 }}>
                        • Results will be refined through detailed ABCDE analysis<br />
                        • This tool does NOT replace professional medical evaluation<br />
                        • <strong>Next step:</strong> Proceed to Advanced Analysis for comprehensive evaluation
                      </Typography>
                    </Alert>

                    {/* Call to Action - Advanced Analysis */}
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Button
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForward />}
                        onClick={() => navigate('/analysis')}
                        sx={{
                          py: 2,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                          '&:hover': {
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
                            boxShadow: `0 6px 24px ${alpha(theme.palette.primary.main, 0.5)}`,
                            transform: 'translateY(-2px)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Continue to Advanced ABCDE Analysis
                      </Button>

                      <Stack direction="row" spacing={2} justifyContent="center">
                        <Button
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => navigate('/history')}
                          sx={{ flex: 1, maxWidth: 250 }}
                        >
                          View History
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<GetApp />}
                          onClick={() => {
                            // TODO: Implement download functionality
                            alert('Download functionality coming soon!');
                          }}
                          sx={{ flex: 1, maxWidth: 250 }}
                        >
                          Download Results
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Stack>
              </Paper>
            </motion.div>
          )}
        </AnimatePresence>

        <Grid container spacing={4}>
          {/* Enhanced File Upload Section */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card
                elevation={0}
                sx={{
                  background: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: 3,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      <PhotoCamera />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Upload Skin Lesion Image
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Drag & drop or click to select
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Enhanced Drag & Drop Area */}
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={handleUploadClick}
                    sx={{
                      border: `2px dashed ${
                        isDragOver
                          ? theme.palette.primary.main
                          : selectedFile
                            ? theme.palette.success.main
                            : alpha(theme.palette.divider, 0.5)
                      }`,
                      borderRadius: 3,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      background: isDragOver
                        ? alpha(theme.palette.primary.main, 0.1)
                        : selectedFile
                          ? alpha(theme.palette.success.main, 0.1)
                          : alpha(theme.palette.background.default, 0.5),
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        background: alpha(theme.palette.primary.main, 0.05),
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                      },
                    }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />

                    <motion.div
                      animate={{
                        scale: isDragOver ? 1.1 : 1,
                        rotate: isDragOver ? 5 : 0
                      }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: selectedFile
                            ? theme.palette.success.main
                            : isDragOver
                              ? theme.palette.primary.main
                              : alpha(theme.palette.text.secondary, 0.1),
                          width: 80,
                          height: 80,
                          mx: 'auto',
                          mb: 2,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {selectedFile ? (
                          <CheckCircle sx={{ fontSize: '2.5rem' }} />
                        ) : (
                          <CloudUpload sx={{ fontSize: '2.5rem' }} />
                        )}
                      </Avatar>
                    </motion.div>

                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {isDragOver
                        ? 'Drop image here'
                        : selectedFile
                          ? selectedFile.name
                          : 'Drop image here or click to browse'
                      }
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Supported formats: JPG, PNG, JPEG • Max size: 10MB
                    </Typography>

                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Chip
                        size="small"
                        icon={<ImageIcon />}
                        label="High Quality"
                        color="primary"
                        variant="outlined"
                      />
                      <Chip
                        size="small"
                        icon={<Security />}
                        label="Secure Upload"
                        color="secondary"
                        variant="outlined"
                      />
                    </Stack>

                    {uploadProgress > 0 && uploadProgress < 100 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Uploading... {uploadProgress}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={uploadProgress}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            '& .MuiLinearProgress-bar': {
                              borderRadius: 4,
                              background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            },
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Enhanced Image Preview */}
                  <AnimatePresence>
                    {previewUrl && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Box sx={{ mt: 3 }}>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                            📷 Image Preview
                          </Typography>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              background: alpha(theme.palette.background.paper, 0.8),
                              border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              borderRadius: 2,
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <img
                              src={previewUrl}
                              alt="Lesion preview"
                              style={{
                                width: '100%',
                                maxHeight: '300px',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
                              }}
                            />
                            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="body2" color="text.secondary">
                                {selectedFile?.name} • {selectedFile && (selectedFile.size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                              <IconButton
                                color="error"
                                onClick={handleRemoveFile}
                                sx={{
                                  background: alpha(theme.palette.error.main, 0.1),
                                  '&:hover': {
                                    background: alpha(theme.palette.error.main, 0.2),
                                  },
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Paper>
                        </Box>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          {/* Enhanced Form Section */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Card
                elevation={0}
                sx={{
                  background: alpha(theme.palette.background.paper, 0.8),
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  borderRadius: 3,
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.error.main} 100%)`,
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                    <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                      <Person />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Patient Information
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        User: {user?.username} • Optional details for better analysis
                      </Typography>
                    </Box>
                  </Stack>

                  <Box component="form" onSubmit={form.handleSubmit(onSubmit)}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="age"
                          control={form.control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Age (optional)"
                              type="number"
                              fullWidth
                              inputProps={{ min: 0, max: 120 }}
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : undefined)}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="body_part"
                          control={form.control}
                          render={({ field }) => (
                            <FormControl
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  },
                                },
                              }}
                            >
                              <InputLabel>Body Part</InputLabel>
                              <Select {...field} label="Body Part">
                                {bodyPartOptions.map((option) => (
                                  <MenuItem key={option} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            background: alpha(theme.palette.info.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                            borderRadius: 2,
                          }}
                        >
                          <Controller
                            name="diameter_larger_than_pencil"
                            control={form.control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    {...field}
                                    checked={field.value}
                                    sx={{
                                      '&.Mui-checked': {
                                        color: theme.palette.primary.main,
                                      },
                                    }}
                                  />
                                }
                                label={
                                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    Diameter exceeds ~6 mm (pencil thickness)?
                                  </Typography>
                                }
                              />
                            )}
                          />
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                            💡 Use a pencil or similar object to estimate the diameter
                          </Typography>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }}>
                          <Chip
                            icon={<LocationOn />}
                            label="Location Information"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        </Divider>
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          variant="outlined"
                          startIcon={isLoadingLocation ? <CircularProgress size={20} /> : <MyLocation />}
                          onClick={getDeviceLocation}
                          disabled={isLoadingLocation}
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            borderWidth: 2,
                            py: 1.5,
                            '&:hover': {
                              borderWidth: 2,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.3)}`,
                            },
                          }}
                        >
                          {isLoadingLocation ? 'Detecting Location...' : 'Auto-detect My Location'}
                        </Button>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="geolocation_city"
                          control={form.control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="City (optional)"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Controller
                          name="geolocation_region"
                          control={form.control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Region (optional)"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Controller
                          name="additional_comment"
                          control={form.control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              label="Additional comments (optional)"
                              multiline
                              rows={4}
                              fullWidth
                              placeholder="Describe any symptoms: itching, color changes, recent growth, bleeding, etc."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  '&:hover': {
                                    transform: 'translateY(-1px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  },
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          fullWidth
                          disabled={!selectedFile || uploadMutation.isPending}
                          startIcon={uploadMutation.isPending ? <CircularProgress size={20} color="inherit" /> : <UploadIcon />}
                          sx={{
                            py: 2,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                            },
                            '&:disabled': {
                              background: alpha(theme.palette.action.disabled, 0.12),
                              transform: 'none',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          {uploadMutation.isPending ? 'Processing Image...' : '🚀 Start AI Analysis'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Error Display */}
                  <AnimatePresence>
                    {uploadMutation.error && (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Alert
                          severity="error"
                          sx={{
                            mt: 3,
                            borderRadius: 2,
                            background: alpha(theme.palette.error.main, 0.05),
                            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {(uploadMutation.error as any)?.response?.data?.detail || 'Upload failed. Please check your connection and try again.'}
                          </Typography>
                        </Alert>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Image Viewer Modal */}
        {uploadResult && uploadResult.image_b64 && (
          <ImageViewerModal
            open={imageModalOpen}
            onClose={() => setImageModalOpen(false)}
            imageBase64={uploadResult.image_b64}
            segmentationResult={uploadResult.segmentation_result}
            preRenderedBase64={uploadResult.segmented_image_b64}
            title="AI Segmentation Result"
            age={uploadResult.age}
            bodyPart={uploadResult.body_part}
            diameterLargerThanPencil={uploadResult.diameter_larger_than_pencil}
            geolocationCity={uploadResult.geolocation_city}
            geolocationRegion={uploadResult.geolocation_region}
            additionalComment={uploadResult.additional_comment}
            timestamp={uploadResult.timestamp}
          />
        )}
      </Box>
    </motion.div>
  );
};

export default UploadPage;
